// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  IntervalType,
  addInterval,
  formatDateLabel,
  subtractInterval,
} from '@/costs/utils/date-utils';
import { DateTime } from 'luxon';

interface TimeSeriesConfig {
  interval: IntervalType;
  today?: DateTime;
}

interface Worker {
  firstName: string;
  lastName: string;
}

interface Project {
  name: string;
}

interface TimeSeriesPoint {
  dates: DateTime[];
  workers: Worker[] | null;
  projects: Project[] | null;
  categories: string[] | null;
  totalAmount: string | null;
  totalMinutes?: number | null;
  totalEntries: number;
  averageAmount: string | null;
  averageMinutes?: number | null;
}

interface ChartDataPoint<T = TimeSeriesPoint> {
  label: string;
  fullLabel: string;
  value: number;
  rawData: T;
}

/**
 * Gets the required time span for the given interval
 */
const getRequiredTimeSpan = (interval: IntervalType): number => {
  switch (interval) {
    case 'daily':
    case 'weekly':
      return 12; // 12 months
    case 'monthly':
    case 'quarterly':
      return 8; // 8 quarters (24 months)
    case 'yearly':
      return 4; // 4 years
    default:
      return 12;
  }
};

/**
 * Calculates the required end date based on interval
 */
const getRequiredEndDate = (
  interval: IntervalType,
  today: DateTime
): DateTime => {
  switch (interval) {
    case 'daily':
    case 'weekly':
      return today;
    case 'monthly':
      return today.endOf('month');
    case 'quarterly':
      return today.endOf('quarter');
    case 'yearly':
      return today.endOf('year');
    default:
      return today;
  }
};

/**
 * Calculates the required start date based on minimum span
 */
const getRequiredStartDate = (
  interval: IntervalType,
  today: DateTime,
  requiredSpan: number
): DateTime => {
  switch (interval) {
    case 'daily':
    case 'weekly':
      // 12 months includes current month, so go back 11 months
      return today.minus({ months: requiredSpan - 1 });
    case 'monthly':
      // 8 quarters includes current quarter, so go back 7 quarters
      return today.minus({ quarters: requiredSpan - 1 }).startOf('month');
    case 'quarterly':
      // 8 quarters includes current quarter, so go back 7 quarters
      return today.minus({ quarters: requiredSpan - 1 }).startOf('quarter');
    case 'yearly':
      // 4 years includes current year, so go back 3 years
      return today.minus({ years: requiredSpan - 1 }).startOf('year');
    default:
      return today.minus({ months: requiredSpan - 1 });
  }
};

/**
 * Extends time series data to meet minimum time span requirements
 */
export const extendTimeSeriesData = <T extends TimeSeriesPoint>(
  inputPoints: T[],
  config: TimeSeriesConfig,
  createEmptyPoint: (date: DateTime) => T
): ChartDataPoint<T>[] => {
  const { interval, today = DateTime.now().startOf('day') } = config;

  if (inputPoints.length === 0) return [];

  // Extract the primary date from each point and create a map
  const dataMap = new Map<string, T>();
  const existingDates: DateTime[] = [];

  inputPoints.forEach((point) => {
    if (point.dates.length > 0) {
      const dateTime = point.dates[0]; // Already a DateTime
      const dateKey = dateTime.toISODate(); // ISO format for consistent keys
      if (dateKey) {
        dataMap.set(dateKey, point);
        existingDates.push(dateTime);
      }
    }
  });

  if (existingDates.length === 0) return [];

  // Find date range from existing data
  const sortedDates = existingDates.sort((a, b) => a.toMillis() - b.toMillis());
  const startDate = sortedDates[0];
  const endDate = sortedDates[sortedDates.length - 1];

  // Generate complete date range by stepping through intervals
  const allDates: DateTime[] = [];
  let currentDate = startDate;

  // Generate dates using the original data range
  while (currentDate <= endDate) {
    allDates.push(currentDate);
    currentDate = addInterval(currentDate, interval);
  }

  // Generate chart data for existing range
  const initialChartData: ChartDataPoint<T>[] = allDates.map((dateTime) => {
    const dateKey = dateTime.toISODate(); // ISO format for consistent keys
    const existingPoint = dataMap.get(dateKey);

    const fullLabel = formatDateLabel(dateTime, interval);

    return {
      label: fullLabel,
      fullLabel,
      value: 0, // Will be set by caller based on their data structure
      rawData: existingPoint || createEmptyPoint(dateTime),
    };
  });

  // Extend data to meet minimum time span requirements
  let extendedData = [...initialChartData];

  // Get required span and dates
  const requiredSpan = getRequiredTimeSpan(interval);
  const requiredEndDate = getRequiredEndDate(interval, today);
  const requiredStartDate = getRequiredStartDate(interval, today, requiredSpan);

  // Forward fill to required end date
  if (extendedData.length > 0) {
    const lastDataDate = extendedData[extendedData.length - 1].rawData.dates[0];
    let nextDate = addInterval(lastDataDate, interval);

    while (nextDate <= requiredEndDate) {
      const fullLabel = formatDateLabel(nextDate, interval);
      extendedData.push({
        label: fullLabel,
        fullLabel,
        value: 0,
        rawData: createEmptyPoint(nextDate),
      });
      nextDate = addInterval(nextDate, interval);
    }
  }

  // Backfill to meet minimum span
  if (extendedData.length > 0) {
    const firstDataDate = extendedData[0].rawData.dates[0];
    const backfillData: ChartDataPoint<T>[] = [];
    let prevDate = subtractInterval(firstDataDate, interval);

    while (prevDate >= requiredStartDate) {
      const fullLabel = formatDateLabel(prevDate, interval);
      backfillData.unshift({
        label: fullLabel,
        fullLabel,
        value: 0,
        rawData: createEmptyPoint(prevDate),
      });
      prevDate = subtractInterval(prevDate, interval);
    }

    extendedData = [...backfillData, ...extendedData];
  }

  return extendedData;
};
